import { createContext, useContext, useState, useEffect, type ReactNode } from 'react'

export type UserRole = 'user' | 'member' | 'admin'

export interface User {
  id: string
  username: string
  email: string
  role: UserRole
  createdAt: string
  lastLogin: string
  emailVerified?: boolean
}

interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (email: string, password: string, recaptchaToken?: string) => Promise<{ success: boolean; error?: string; needsVerification?: boolean }>
  register: (username: string, email: string, password: string, recaptchaToken?: string) => Promise<{ success: boolean; error?: string; needsVerification?: boolean }>
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
  hasRole: (role: UserRole | UserRole[]) => boolean
  isAdmin: boolean
  isMember: boolean
  needsVerification: boolean
  verificationEmail: string | null
  resendVerification: (email: string) => Promise<{ success: boolean; error?: string; retryAfter?: number }>
  clearVerification: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [needsVerification, setNeedsVerification] = useState(false)
  const [verificationEmail, setVerificationEmail] = useState<string | null>(null)

  const refreshUser = async () => {
    try {
      const res = await fetch('/api/auth/me', { credentials: 'include' })
      if (res.ok) {
        const data = await res.json()
        setUser(data.user || null)
      } else {
        setUser(null)
      }
    } catch {
      setUser(null)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    refreshUser()
  }, [])

  const login = async (email: string, password: string, recaptchaToken?: string): Promise<{ success: boolean; error?: string; needsVerification?: boolean }> => {
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password, recaptchaToken }),
      })
      const data = await res.json()
      if (res.ok) {
        setUser(data.user)
        setNeedsVerification(false)
        setVerificationEmail(null)
        return { success: true }
      }
      if (res.status === 403 && data.needsVerification) {
        setNeedsVerification(true)
        setVerificationEmail(data.email || email)
        return { success: false, error: data.error || 'Email verification required', needsVerification: true }
      }
      return { success: false, error: data.error || 'Login failed' }
    } catch {
      return { success: false, error: 'Network error. Make sure the server is running.' }
    }
  }

  const register = async (username: string, email: string, password: string, recaptchaToken?: string): Promise<{ success: boolean; error?: string; needsVerification?: boolean }> => {
    try {
      const res = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ username, email, password, recaptchaToken }),
      })
      const data = await res.json()
      if (res.ok) {
        if (data.needsVerification) {
          setNeedsVerification(true)
          setVerificationEmail(email)
          return { success: true, needsVerification: true }
        }
        setUser(data.user)
        return { success: true }
      }
      return { success: false, error: data.error || 'Registration failed' }
    } catch {
      return { success: false, error: 'Network error. Make sure the server is running.' }
    }
  }

  const resendVerification = async (email: string): Promise<{ success: boolean; error?: string; retryAfter?: number }> => {
    try {
      const res = await fetch('/api/auth/resend-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email }),
      })
      const data = await res.json()
      if (res.ok) {
        return { success: true }
      }
      return { success: false, error: data.error, retryAfter: data.retryAfter }
    } catch {
      return { success: false, error: 'Network error.' }
    }
  }

  const clearVerification = () => {
    setNeedsVerification(false)
    setVerificationEmail(null)
  }

  const logout = async () => {
    try {
      await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' })
    } catch {
      // silent
    }
    setUser(null)
  }

  const hasRole = (role: UserRole | UserRole[]): boolean => {
    if (!user) return false
    const roles = Array.isArray(role) ? role : [role]
    return roles.includes(user.role)
  }

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated: !!user,
      isLoading,
      login,
      register,
      logout,
      refreshUser,
      hasRole,
      isAdmin: user?.role === 'admin',
      isMember: user?.role === 'member' || user?.role === 'admin',
      needsVerification,
      verificationEmail,
      resendVerification,
      clearVerification,
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
